import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Users, DollarSign, Phone, MessageSquare, TrendingUp, ShieldAlert, CreditCard, Gift, X, ArrowRightLeft, Download, Printer, Megaphone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import MarketingStudio from "./MarketingStudio";
import AdminFranchises from "./AdminFranchises";
import PayFirstPanel, { chargeOrPause } from "../components/PayFirstPanel";

export default function Admin() {
    const { user, loading } = useAuth();
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [txns, setTxns] = useState([]);
    const [accounting, setAccounting] = useState(null);
    const [portReqs, setPortReqs] = useState([]);
    const [portStatus, setPortStatus] = useState(null);
    const [telnyxPort, setTelnyxPort] = useState(null);
    const [ads, setAds] = useState([]);
    const [mobileSubs, setMobileSubs] = useState([]);
    const [attachForm, setAttachForm] = useState({ email: "", phone_number: "", plan_id: "flex_3gb", esim_sim_id: "", esim_iccid: "" });
    const [attaching, setAttaching] = useState(false);
    const [adminOverride, setAdminOverride] = useState(false);
    const [dunning, setDunning] = useState([]);
    const [apiKeys, setApiKeys] = useState([]);
    const [newKeyName, setNewKeyName] = useState("");
    const [newKeyScopes, setNewKeyScopes] = useState(["read"]);
    const [createdKey, setCreatedKey] = useState(null);
    const [marketing, setMarketing] = useState({ ga4_id: "", google_ads_id: "", google_ads_purchase_label: "", meta_pixel_id: "", bing_uet_id: "" });
    const [savingMarketing, setSavingMarketing] = useState(false);
    const [social, setSocial] = useState({ instagram: "", facebook: "", twitter: "", tiktok: "", linkedin: "", youtube: "" });
    const [savingSocial, setSavingSocial] = useState(false);
    const [telnyxSettings, setTelnyxSettings] = useState(null);
    const [telnyxReadiness, setTelnyxReadiness] = useState(null);
    const [telnyxStatus, setTelnyxStatus] = useState(null);
    const [telnyxForm, setTelnyxForm] = useState({ api_key: "", public_key: "" });
    const [savingTelnyx, setSavingTelnyx] = useState(false);
    const [tab, setTab] = useState("overview");
    const [detail, setDetail] = useState(null); // {user, ssn, numbers, transactions}

    useEffect(() => {
        if (!user?.is_admin) return;
        load();
    }, [user]);

    const load = async () => {
        try {
            const [s, u, t, a, p] = await Promise.all([
                api.get("/admin/stats"),
                api.get("/admin/users"),
                api.get("/admin/transactions"),
                api.get("/admin/accounting"),
                api.get("/admin/porting"),
            ]);
            setStats(s.data);
            setUsers(u.data);
            setTxns(t.data);
            setAccounting(a.data);
            setPortReqs(p.data);
        } catch (e) {
            toast.error("Failed to load admin data");
        }
        // Marketing config (non-blocking)
        try {
            const m = await api.get("/marketing/config");
            setMarketing((prev) => ({ ...prev, ...m.data }));
        } catch {}
        // Advertiser campaigns (non-blocking)
        try {
            const ad = await api.get("/admin/ads");
            setAds(ad.data);
        } catch {}
        // Social links (non-blocking)
        try {
            const sc = await api.get("/social/config");
            setSocial((prev) => ({ ...prev, ...sc.data }));
        } catch {}
        // API keys (non-blocking)
        try {
            const k = await api.get("/admin/api-keys");
            setApiKeys(k.data);
        } catch {}
        // Mobile subscriptions (non-blocking)
        try {
            const m = await api.get("/admin/mobile/subscriptions");
            setMobileSubs(m.data);
        } catch {}
        // Dunning / past-due lines (non-blocking)
        try {
            const dn = await api.get("/admin/dunning");
            setDunning(dn.data.lines || []);
        } catch {}
        // Twilio porting eligibility (separate — non-blocking, may be slow)
        try {
            const ps = await api.get("/admin/twilio/porting-status");
            setPortStatus(ps.data);
        } catch {}
        // Telnyx porting engine (primary) status
        try {
            const tx = await api.get("/admin/telnyx/porting-status");
            setTelnyxPort(tx.data);
        } catch {}
        // Telnyx system settings + live readiness (non-blocking)
        try {
            const ts = await api.get("/admin/settings/telnyx");
            setTelnyxSettings(ts.data);
        } catch {}
        try {
            const rd = await api.get("/admin/telnyx/readiness");
            setTelnyxReadiness(rd.data);
        } catch {}
        try {
            const st = await api.get("/admin/telnyx/status");
            setTelnyxStatus(st.data);
        } catch {}
    };

    const saveTelnyx = async (extra = {}) => {
        setSavingTelnyx(true);
        try {
            const payload = { ...extra };
            if (telnyxForm.api_key.trim()) payload.api_key = telnyxForm.api_key.trim();
            if (telnyxForm.public_key.trim()) payload.public_key = telnyxForm.public_key.trim();
            const { data } = await api.put("/admin/settings/telnyx", payload);
            setTelnyxSettings(data);
            setTelnyxForm({ api_key: "", public_key: "" });
            toast.success("Telnyx settings saved");
            const rd = await api.get("/admin/telnyx/readiness");
            setTelnyxReadiness(rd.data);
            try { const st = await api.get("/admin/telnyx/status"); setTelnyxStatus(st.data); } catch {}
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save Telnyx settings");
        }
        setSavingTelnyx(false);
    };

    const [repairing, setRepairing] = useState(false);
    const [repairResult, setRepairResult] = useState(null);
    const [voiceFwd, setVoiceFwd] = useState("");
    const [savingFwd, setSavingFwd] = useState(false);
    const [spamShield, setSpamShield] = useState(false);
    const [savingShield, setSavingShield] = useState(false);
    const [sandbox, setSandbox] = useState(false);
    const [savingSandbox, setSavingSandbox] = useState(false);
    const [esimNumber, setEsimNumber] = useState("");
    const [esimIccid, setEsimIccid] = useState("");
    const [assigningEsim, setAssigningEsim] = useState(false);
    const assignNumberToEsim = async () => {
        if (!esimNumber.trim()) return;
        setAssigningEsim(true);
        try {
            const { data } = await api.post("/admin/mobile/assign-number-to-esim", { phone_number: esimNumber, iccid: esimIccid });
            toast.success(data.message || "Number queued for eSIM activation");
            setEsimNumber(""); setEsimIccid("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not assign");
        }
        setAssigningEsim(false);
    };
    const [vipContacts, setVipContacts] = useState([]);
    const [vipNumber, setVipNumber] = useState("");
    const [vipLabel, setVipLabel] = useState("");
    const [addingVip, setAddingVip] = useState(false);
    useEffect(() => {
        api.get("/admin/telnyx/voice-forward").then(({ data }) => setVoiceFwd(data.forward_number || "")).catch(() => {});
        api.get("/admin/telnyx/spam-shield").then(({ data }) => setSpamShield(!!data.enabled)).catch(() => {});
        api.get("/admin/sandbox").then(({ data }) => setSandbox(!!data.enabled)).catch(() => {});
        api.get("/admin/telnyx/vip").then(({ data }) => setVipContacts(data.contacts || [])).catch(() => {});
    }, []);
    const addVip = async () => {
        if (!vipNumber.trim()) return;
        setAddingVip(true);
        try {
            const { data } = await api.post("/admin/telnyx/vip", { number: vipNumber, label: vipLabel });
            setVipContacts(data.contacts || []);
            setVipNumber(""); setVipLabel("");
            toast.success("VIP contact added — they'll always ring through");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not add");
        }
        setAddingVip(false);
    };
    const removeVip = async (number) => {
        try {
            const { data } = await api.post("/admin/telnyx/vip/remove", { number });
            setVipContacts(data.contacts || []);
            toast.success("VIP contact removed");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not remove");
        }
    };
    const toggleSpamShield = async () => {
        const next = !spamShield;
        setSavingShield(true);
        try {
            const { data } = await api.post("/admin/telnyx/spam-shield", { enabled: next });
            setSpamShield(!!data.enabled);
            toast.success(data.enabled ? "Spam Shield ON — callers must press 1 to connect" : "Spam Shield OFF");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save");
        }
        setSavingShield(false);
    };
    const toggleSandbox = async () => {
        const next = !sandbox;
        setSavingSandbox(true);
        try {
            const { data } = await api.post("/admin/sandbox", { enabled: next });
            setSandbox(!!data.enabled);
            toast.success(data.enabled ? "Sandbox ON — flows run in test mode" : "Sandbox OFF — live mode");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save");
        }
        setSavingSandbox(false);
    };
    const saveVoiceFwd = async () => {
        setSavingFwd(true);
        try {
            const { data } = await api.post("/admin/telnyx/voice-forward", { forward_number: voiceFwd });
            setVoiceFwd(data.forward_number || "");
            toast.success(data.forward_number ? `Incoming calls will ring ${data.forward_number}` : "Cleared — incoming calls ring the in-app phone");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save");
        }
        setSavingFwd(false);
    };
    const repairCalling = async () => {
        setRepairing(true);
        setRepairResult(null);
        try {
            const { data } = await api.post("/admin/telnyx/repair-calling");
            setRepairResult(data);
            toast.success(data.ok ? "Calling repaired — test a call now" : "Repair ran with warnings — see details");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not repair calling");
        }
        setRepairing(false);
    };

    const confirmPort = async (id) => {
        if (!window.confirm("Submit this number-port order to the carrier? (Requires live provisioning enabled.)")) return;
        try {
            await api.post(`/admin/mobile/${id}/port/confirm`);
            toast.success("Port submitted to carrier");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not confirm port");
        }
    };

    const runPortDayAlerts = async () => {
        try {
            const { data } = await api.post(`/admin/mobile/port-day-alerts/run`);
            toast.success(`Port-day alerts sent to ${data.alerted} line(s)`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not send port-day alerts");
        }
    };

    const fixPortMessaging = async (id) => {
        try {
            await api.post(`/admin/mobile/${id}/port/fix-messaging`);
            toast.success("Messaging profile assigned to the porting order — retry Submit to carrier");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not fix messaging profile");
        }
    };

    const markPortComplete = async (id) => {
        if (!window.confirm("Mark this port complete and activate the line now?")) return;
        try {
            await api.post(`/admin/mobile/${id}/port/complete`);
            toast.success("Line activated");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not complete port");
        }
    };

    const reissueEsim = async (id, replace = false) => {
        if (replace && !window.confirm("Issue a brand-new eSIM for this line? The old eSIM profile will be deleted and the customer must install the new one.")) return;
        try {
            const { data } = await api.post(`/admin/mobile/${id}/esim/reissue?replace=${replace}&override=${adminOverride}`);
            toast.success(data.emailed ? "eSIM QR refreshed and emailed to customer" : "eSIM QR refreshed for customer");
            setAdminOverride(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not re-issue eSIM");
        }
    };

    const attachLine = async () => {
        if (!attachForm.email || !attachForm.phone_number) { toast.error("Email and phone number are required"); return; }
        setAttaching(true);
        try {
            await api.post("/admin/mobile/attach-line", { ...attachForm, override_payment: adminOverride });
            toast.success(`Line ${attachForm.phone_number} attached & activated`);
            setAdminOverride(false);
            setAttachForm({ email: "", phone_number: "", plan_id: "flex_3gb", esim_sim_id: "", esim_iccid: "" });
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not attach line");
        }
        setAttaching(false);
    };

    const togglePaymentExempt = async (id, enabled) => {
        try {
            await api.post(`/admin/mobile/${id}/payment-override`, { enabled });
            toast.success(enabled ? "Line exempted from the payment check" : "Payment check re-enabled for this line");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    };

    const sweepUnpaid = async () => {
        if (!window.confirm("Pause every active line with no payment on file and text each customer a pay link?")) return;
        try {
            const { data } = await api.post("/admin/mobile/sweep-unpaid");
            toast.success(`Checked ${data.checked} active line(s): ${data.paused} newly paused, ${data.charged_now} charged now, ${data.already_paused} already paused awaiting payment`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Sweep failed"); }
    };

    const resendCardLink = async (id) => {
        try {
            await api.post(`/admin/mobile/${id}/resend-card-link`);
            toast.success("Update-card link sent");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not send link");
        }
    };


    const updatePort = async (id, status) => {        let admin_note;
        if (status === "rejected") {
            admin_note = window.prompt("Reason for rejection (sent to customer):", "") || "";
        }
        try {
            await api.patch(`/admin/porting/${id}`, { status, ...(admin_note !== undefined ? { admin_note } : {}) });
            toast.success(`Port marked ${status.replace(/_/g, " ")}`);
            load();
        } catch { toast.error("Failed to update port"); }
    };

    const cancelSub = async (userId) => {
        if (!window.confirm("Cancel this user's subscription? They'll be downgraded to Free.")) return;
        try {
            await api.post(`/admin/user/${userId}/cancel-subscription`);
            toast.success("Subscription cancelled");
            load();
        } catch { toast.error("Failed"); }
    };

    const creditSms = async (userId) => {
        const amount = parseInt(window.prompt("How many SMS credits to add?", "100"), 10);
        if (!amount) return;
        try {
            await api.post(`/admin/user/${userId}/credit-sms?amount=${amount}`);
            toast.success(`Added ${amount} credits`);
            load();
        } catch { toast.error("Failed"); }
    };

    const terminate = async (u) => {
        if (!window.confirm(`TERMINATE ${u.email}?\n\nThis cancels their subscription, releases all their phone numbers, and blocks their account. This cannot be undone.`)) return;
        try {
            const { data } = await api.post(`/admin/user/${u.user_id}/terminate`);
            toast.success(`Service terminated — ${data.numbers_released} number(s) released`);
            setDetail(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed to terminate"); }
    };

    const viewCustomer = async (userId) => {
        try {
            const { data } = await api.get(`/admin/user/${userId}`);
            setDetail(data);
        } catch { toast.error("Failed to load customer"); }
    };

    const saveMarketing = async () => {
        setSavingMarketing(true);
        try {
            await api.put("/admin/marketing", marketing);
            toast.success("Tracking IDs saved — pixels go live on next page load");
        } catch {
            toast.error("Failed to save tracking IDs");
        } finally {
            setSavingMarketing(false);
        }
    };

    const moderateAd = async (id, status) => {        let admin_note = "";
        if (status === "rejected") {
            admin_note = window.prompt("Reason for rejection (shown to advertiser):", "") || "";
        }
        try {
            await api.patch(`/admin/ads/${id}`, { status, admin_note });
            toast.success(`Ad ${status === "approved" ? "approved" : status === "rejected" ? "rejected" : "paused"}`);
            load();
        } catch { toast.error("Failed to update ad"); }
    };

    const saveSocial = async () => {        setSavingSocial(true);
        try {
            await api.put("/admin/social", social);
            toast.success("Social links saved — they'll show in the site footer");
        } catch {
            toast.error("Failed to save social links");
        } finally {
            setSavingSocial(false);
        }
    };

    const toggleScope = (s) => setNewKeyScopes((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);

    const createApiKey = async () => {
        if (!newKeyName.trim()) { toast.error("Give the key a name"); return; }
        try {
            const { data } = await api.post("/admin/api-keys", { name: newKeyName, scopes: newKeyScopes.length ? newKeyScopes : ["read"] });
            setCreatedKey(data);
            setNewKeyName("");
            setNewKeyScopes(["read"]);
            load();
        } catch { toast.error("Failed to create API key"); }
    };

    const revokeApiKey = async (id) => {
        if (!window.confirm("Revoke this key? Any app using it will immediately stop working.")) return;
        try {
            await api.delete(`/admin/api-keys/${id}`);
            toast.success("Key revoked");
            load();
        } catch { toast.error("Failed to revoke key"); }
    };

    if (loading) return <div className="text-zinc-500">Loading…</div>;
    if (!user) return <Navigate to="/" replace />;
    if (!user.is_admin) {
        return (
            <div className="border border-red-200 bg-red-50 rounded-sm p-10 text-center" data-testid="admin-denied">
                <ShieldAlert className="w-8 h-8 mx-auto text-red-500 mb-3" />
                <p className="font-display text-2xl">Admin access required</p>
                <p className="text-zinc-600 mt-2">Signed in as {user.email}.</p>
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="admin-root">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-deep mb-2">— Back office</p>
                    <h1 className="font-display text-4xl font-light tracking-tight">
                        Admin <span className="font-serif-italic">console.</span>
                    </h1>
                </div>
                <div className="flex gap-2">
                    <a href="/brand" target="_blank" rel="noopener noreferrer" data-testid="admin-brand-link"
                        className="h-9 px-3 rounded-sm text-xs inline-flex items-center gap-1.5 border border-[#DDD6FE] bg-white hover:bg-cream">
                        <Download className="w-3.5 h-3.5" /> Brand & logo downloads
                    </a>
                    <a href="/poster" target="_blank" rel="noopener noreferrer" data-testid="admin-poster-link"
                        className="h-9 px-3 rounded-sm text-xs inline-flex items-center gap-1.5 border border-[#DDD6FE] bg-white hover:bg-cream">
                        <Printer className="w-3.5 h-3.5" /> Marketing poster
                    </a>
                    <a href="/ads-poster" target="_blank" rel="noopener noreferrer" data-testid="admin-ads-poster-link"
                        className="h-9 px-3 rounded-sm text-xs inline-flex items-center gap-1.5 border border-[#DDD6FE] bg-white hover:bg-cream">
                        <Printer className="w-3.5 h-3.5" /> Advertiser poster
                    </a>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b border-[#DDD6FE] overflow-x-auto">
                {["overview", "customers", "porting", "transactions", "accounting", "ads", "mobile", "studio", "franchise", "marketing", "settings", "api"].map((t) => (
                    <button
                        key={t}
                        onClick={() => setTab(t)}
                        data-testid={`admin-tab-${t}`}
                        className={`px-4 py-2 text-sm border-b-2 transition-colors capitalize whitespace-nowrap ${
                            tab === t ? "border-emerald-deep text-emerald-deep" : "border-transparent text-zinc-500 hover:text-zinc-800"
                        }`}
                    >
                        {t === "franchise" ? "dealers" : t}
                    </button>
                ))}
            </div>

            {tab === "overview" && stats && (
                <>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#DDD6FE] border border-[#DDD6FE]" data-testid="admin-stats-grid">
                        <Stat label="Total users" value={stats.total_users} icon={Users} />
                        <Stat label="Paid users" value={stats.paid_users} icon={Users} highlight />
                        <Stat label="MRR" value={`$${stats.mrr.toFixed(2)}`} icon={TrendingUp} highlight />
                        <Stat label="All-time revenue" value={`$${stats.total_revenue.toFixed(2)}`} icon={DollarSign} />
                        <Stat label="Active numbers" value={stats.total_numbers} icon={Phone} />
                        <Stat label="Messages sent" value={stats.total_messages} icon={MessageSquare} />
                        <Stat label="On Plus" value={stats.users_by_plan.plus} icon={CreditCard} />
                        <Stat label="On Pro" value={stats.users_by_plan.pro} icon={Gift} />
                    </div>
                    <div className="border border-[#DDD6FE] rounded-sm bg-paper p-6">
                        <h3 className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-4">Plan distribution</h3>
                        <ul className="space-y-2">
                            {Object.entries(stats.users_by_plan).map(([plan, count]) => (
                                <li key={plan} className="flex items-center justify-between text-sm">
                                    <span className="capitalize">{plan}</span>
                                    <span className="font-mono">{count}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="border border-[#DDD6FE] rounded-sm bg-paper p-6" data-testid="admin-dunning-widget">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">Suspended / past-due lines</h3>
                            <span className={`font-mono text-[10px] uppercase px-2 py-0.5 rounded-sm ${dunning.length ? "bg-[#D97706] text-white" : "bg-[#ECFDF5] text-brand-green border border-brand-green"}`} data-testid="dunning-count">
                                {dunning.length ? `${dunning.length} need attention` : "All current"}
                            </span>
                        </div>
                        {dunning.length === 0 ? (
                            <p className="text-sm text-zinc-500">No lines in dunning — everyone's paid up. 🎉</p>
                        ) : (
                            <ul className="divide-y divide-[#EDE7F6]">
                                {dunning.map((d) => (
                                    <li key={d.mobile_sub_id} className="flex items-center justify-between py-3 gap-3" data-testid={`dunning-${d.mobile_sub_id}`}>
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium truncate">{d.user_email || d.user_id}</p>
                                            <p className="text-xs text-zinc-500 font-mono">{d.phone_number || "—"} · {d.plan_id} · stage {d.dunning_stage}</p>
                                        </div>
                                        <Button onClick={() => resendCardLink(d.mobile_sub_id)} data-testid={`dunning-resend-${d.mobile_sub_id}`}
                                            className="bg-[#4C1D95] text-white rounded-sm h-8 text-xs shrink-0">Resend card link</Button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </>
            )}

            {tab === "customers" && (
                <div className="border border-[#DDD6FE] rounded-sm bg-paper overflow-x-auto" data-testid="admin-users-table">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-[#DDD6FE] bg-cream/40">
                                <Th>Email</Th><Th>Name</Th><Th>Plan</Th><Th>Profile</Th><Th>Credits</Th><Th>Status</Th><Th>Joined</Th><Th>Actions</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => (
                                <tr key={u.user_id} className="border-b border-[#DDD6FE]/60" data-testid={`admin-user-${u.user_id}`}>
                                    <Td><span className="font-mono text-xs">{u.email}</span>{u.is_admin && <span className="ml-2 font-mono text-[9px] text-emerald-deep">ADMIN</span>}</Td>
                                    <Td><span className="text-sm">{u.full_name || u.name}</span></Td>
                                    <Td><span className="font-display capitalize">{u.plan}</span></Td>
                                    <Td>
                                        <span className={`font-mono text-[10px] uppercase px-1.5 py-0.5 rounded-sm border ${u.profile_completed ? "text-emerald-deep border-emerald-deep" : "text-zinc-400 border-[#DDD6FE]"}`}>
                                            {u.profile_completed ? "KYC ✓" : "Incomplete"}
                                        </span>
                                    </Td>
                                    <Td><span className="font-mono">{u.sms_credits}</span></Td>
                                    <Td>
                                        {u.is_terminated ? (
                                            <span className="font-mono text-[10px] uppercase text-red-600 border border-red-300 px-1.5 py-0.5 rounded-sm">Terminated</span>
                                        ) : (
                                            <span className="font-mono text-[10px] uppercase text-zinc-500">Active</span>
                                        )}
                                    </Td>
                                    <Td><span className="font-mono text-[10px] text-zinc-500">{new Date(u.created_at).toLocaleDateString()}</span></Td>
                                    <Td>
                                        <div className="flex gap-1">
                                            <Button onClick={() => viewCustomer(u.user_id)} variant="outline" data-testid={`view-${u.user_id}`} className="rounded-sm h-7 px-2 text-xs border-[#DDD6FE]">View</Button>
                                            <Button onClick={() => creditSms(u.user_id)} variant="outline" data-testid={`credit-${u.user_id}`} className="rounded-sm h-7 px-2 text-xs border-[#DDD6FE]">+SMS</Button>
                                            {u.plan !== "free" && (
                                                <Button onClick={() => cancelSub(u.user_id)} variant="outline" data-testid={`cancel-sub-${u.user_id}`} className="rounded-sm h-7 px-2 text-xs border-[#DDD6FE]">Cancel</Button>
                                            )}
                                            {!u.is_admin && !u.is_terminated && (
                                                <Button onClick={() => terminate(u)} variant="outline" data-testid={`terminate-${u.user_id}`} className="rounded-sm h-7 px-2 text-xs border-red-300 text-red-600 hover:bg-red-50">Terminate</Button>
                                            )}
                                        </div>
                                    </Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === "porting" && (
                <div className="space-y-4">
                    {/* Telnyx = primary porting engine */}
                    {telnyxPort && (
                        <div
                            className={`border rounded-sm p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
                                telnyxPort.configured ? "border-emerald-deep bg-[#ECFDF5]" : "border-[#D97706] bg-[#FEF3C7]"
                            }`}
                            data-testid="telnyx-porting-status"
                        >
                            <div className="text-sm">
                                <p className={`font-medium ${telnyxPort.configured ? "text-emerald-deep" : "text-[#D97706]"}`}>
                                    {telnyxPort.configured ? "✓ Telnyx is the primary porting engine (keep-your-number)" : "Telnyx porting is NOT configured"}
                                </p>
                                <p className="text-zinc-600 mt-1">{telnyxPort.message}</p>
                            </div>
                            <a href={telnyxPort.console_url} target="_blank" rel="noopener noreferrer"
                                data-testid="telnyx-console-link"
                                className="h-9 px-3 rounded-sm text-xs inline-flex items-center bg-[#4C1D95] text-white hover:bg-[#4C1D95]/90 shrink-0">
                                Open Telnyx port console →
                            </a>
                        </div>
                    )}
                    {/* Twilio = secondary second-number provider (legacy port-in path) */}
                    {portStatus && (
                        <div
                            className={`border rounded-sm p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
                                portStatus.can_port_in ? "border-emerald-deep bg-[#ECFDF5]" : "border-[#DDD6FE] bg-paper"
                            }`}
                            data-testid="twilio-porting-status"
                        >
                            <div className="text-sm">
                                <p className={`font-medium ${portStatus.can_port_in ? "text-emerald-deep" : "text-zinc-700"}`}>
                                    {portStatus.can_port_in
                                        ? "✓ Twilio (secondary second-number provider) can also accept port-ins"
                                        : "Twilio (secondary second-number provider) — port-ins not enabled"}
                                </p>
                                <p className="text-zinc-600 mt-1">
                                    Twilio is only used for optional second phone numbers. Number porting for wireless runs on Telnyx above.{" "}
                                    {portStatus.bundles && portStatus.bundles.length > 0 ? (
                                        <>Twilio regulatory bundle: {portStatus.bundles.map((b) => `${b.friendly_name || b.sid} (${b.status})`).join(", ")}.</>
                                    ) : (
                                        <>No Twilio regulatory bundle found.</>
                                    )}
                                </p>
                            </div>
                            <div className="flex gap-2 shrink-0">
                                <a href={portStatus.console_bundles_url} target="_blank" rel="noopener noreferrer"
                                    data-testid="twilio-bundles-link"
                                    className="h-9 px-3 rounded-sm text-xs inline-flex items-center border border-[#DDD6FE] bg-white hover:bg-cream">
                                    Fix regulatory bundle →
                                </a>
                                <a href={portStatus.console_porting_url} target="_blank" rel="noopener noreferrer"
                                    data-testid="twilio-console-link"
                                    className="h-9 px-3 rounded-sm text-xs inline-flex items-center bg-[#4C1D95] text-white hover:bg-[#4C1D95]/90">
                                    Open Twilio port-in console →
                                </a>
                            </div>
                        </div>
                    )}

                    <div className="border border-[#DDD6FE] rounded-sm bg-paper overflow-x-auto" data-testid="admin-porting-table">
                    {portReqs.length === 0 ? (
                        <div className="p-10 text-center text-sm text-zinc-500">No port requests yet.</div>
                    ) : (
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-[#DDD6FE] bg-cream/40">
                                    <Th>Number</Th><Th>Dir</Th><Th>Carrier</Th><Th>Holder</Th><Th>Account / PIN</Th><Th>ZIP</Th><Th>Status</Th><Th>Actions</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {portReqs.map((p) => (
                                    <tr key={p.request_id} className="border-b border-[#DDD6FE]/60 align-top" data-testid={`admin-port-${p.request_id}`}>
                                        <Td><span className="font-mono text-sm">{p.phone_number}</span></Td>
                                        <Td><span className="font-mono text-[10px] uppercase">{p.direction}</span></Td>
                                        <Td><span className="text-xs">{p.current_carrier || p.carrier_lookup?.carrier_name || "—"}<br /><span className="text-zinc-400">{p.carrier_lookup?.line_type || ""}</span></span></Td>
                                        <Td><span className="text-xs">{p.account_holder_name}</span></Td>
                                        <Td><span className="font-mono text-xs">{p.account_number || "—"}<br /><span className="text-zinc-500">PIN {p.account_pin || "—"}</span></span></Td>
                                        <Td><span className="font-mono text-xs">{p.billing_zip}</span></Td>
                                        <Td>
                                            <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 rounded-sm border border-[#DDD6FE]">{(p.status || "").replace(/_/g, " ")}</span>
                                            {p.admin_note && <div className="text-[10px] text-zinc-500 mt-1 italic max-w-[140px]">{p.admin_note}</div>}
                                        </Td>
                                        <Td>
                                            <div className="flex flex-wrap gap-1 max-w-[240px]">
                                                <a href={portStatus?.console_porting_url || "https://console.twilio.com/us1/develop/phone-numbers/port-and-host/port-ins"}
                                                    target="_blank" rel="noopener noreferrer"
                                                    data-testid={`port-twilio-${p.request_id}`}
                                                    className="rounded-sm h-7 px-2 text-[10px] inline-flex items-center bg-[#4C1D95] text-white hover:bg-[#4C1D95]/90">
                                                    Port in Twilio →
                                                </a>
                                                {["in_review", "submitted_to_carrier", "completed", "rejected"].map((s) => (
                                                    <Button key={s} onClick={() => updatePort(p.request_id, s)} variant="outline"
                                                        data-testid={`port-set-${s}-${p.request_id}`}
                                                        className={`rounded-sm h-7 px-2 text-[10px] border-[#DDD6FE] ${p.status === s ? "bg-cream" : ""}`}>
                                                        {s.replace(/_/g, " ")}
                                                    </Button>
                                                ))}
                                            </div>
                                        </Td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    </div>
                </div>
            )}

            {tab === "transactions" && (
                <div className="border border-[#DDD6FE] rounded-sm bg-paper overflow-x-auto" data-testid="admin-txns-table">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-[#DDD6FE] bg-cream/40">
                                <Th>When</Th><Th>User</Th><Th>Kind</Th><Th>Amount</Th><Th>Status</Th><Th>Description</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {txns.map((t) => (
                                <tr key={t.transaction_id || t.session_id} className="border-b border-[#DDD6FE]/60" data-testid={`admin-txn-${t.transaction_id || t.session_id}`}>
                                    <Td><span className="font-mono text-[10px] text-zinc-500">{new Date(t.created_at).toLocaleString()}</span></Td>
                                    <Td><span className="font-mono text-xs">{(t.user_id || "").slice(-12)}</span></Td>
                                    <Td><span className="font-mono text-xs uppercase">{t.kind}</span></Td>
                                    <Td><span className="font-mono">${(t.amount || 0).toFixed(2)}</span></Td>
                                    <Td>
                                        <span className={`font-mono text-[10px] uppercase px-1.5 py-0.5 rounded-sm border ${
                                            t.payment_status === "paid"
                                                ? "text-emerald-deep border-emerald-deep"
                                                : "text-zinc-500 border-[#DDD6FE]"
                                        }`}>
                                            {t.payment_status}
                                        </span>
                                    </Td>
                                    <Td><span className="text-xs">{t.description}</span></Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {tab === "accounting" && accounting && (
                <div className="space-y-6" data-testid="admin-accounting">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#DDD6FE] border border-[#DDD6FE]">
                        <Stat label="All-time revenue" value={`$${accounting.total_revenue.toFixed(2)}`} icon={DollarSign} highlight />
                        <Stat label="Last 30 days" value={`$${accounting.last_30_days_revenue.toFixed(2)}`} icon={TrendingUp} highlight />
                        <Stat label="Paying customers" value={accounting.paying_customers} icon={Users} />
                        <Stat label="Avg revenue / customer" value={`$${accounting.avg_revenue_per_customer.toFixed(2)}`} icon={CreditCard} />
                    </div>
                    <div className="border border-[#DDD6FE] rounded-sm bg-paper p-6">
                        <h3 className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-4">Revenue by product</h3>
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-[#DDD6FE]"><Th>Product</Th><Th>Paid transactions</Th><Th>Revenue</Th></tr>
                            </thead>
                            <tbody>
                                {Object.entries(accounting.revenue_by_kind).map(([kind, v]) => (
                                    <tr key={kind} className="border-b border-[#DDD6FE]/60">
                                        <Td><span className="font-mono text-xs uppercase">{kind}</span></Td>
                                        <Td><span className="font-mono">{v.count}</span></Td>
                                        <Td><span className="font-mono">${v.revenue.toFixed(2)}</span></Td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <p className="text-xs text-zinc-500 mt-4">
                            {accounting.paid_transactions} paid transactions all-time · {accounting.last_30_days_transactions} in the last 30 days
                        </p>
                    </div>
                </div>
            )}

            {tab === "ads" && (
                <div className="space-y-3" data-testid="admin-ads">
                    {ads.length === 0 ? (
                        <div className="border border-[#DDD6FE] rounded-sm bg-paper p-10 text-center text-sm text-zinc-500">No ad campaigns submitted yet.</div>
                    ) : (
                        ads.map((a) => {
                            const badge = {
                                pending_review: "text-amber-600 border-amber-300",
                                approved: "text-emerald-deep border-emerald-deep",
                                active: "text-emerald-deep border-emerald-deep",
                                rejected: "text-red-600 border-red-300",
                                inactive: "text-zinc-500 border-[#DDD6FE]",
                            }[a.status] || "text-zinc-500 border-[#DDD6FE]";
                            return (
                                <div key={a.campaign_id} className="border border-[#DDD6FE] rounded-sm bg-paper p-5" data-testid={`admin-ad-${a.campaign_id}`}>
                                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-display text-lg">{a.headline}</span>
                                                <span className={`font-mono text-[10px] uppercase px-1.5 py-0.5 rounded-sm border ${badge}`}>{(a.status || "").replace(/_/g, " ")}</span>
                                            </div>
                                            <p className="text-sm text-zinc-600 mt-0.5">{a.business_name} · {a.body}</p>
                                            <p className="text-xs text-zinc-500 mt-1 font-mono">
                                                {a.placement === "directory" ? "Deals page" : a.placement === "inbox" ? "Inbox" : "Dashboard"} · {a.billing_model === "one_time" ? "one-time" : "subscription"}
                                                {a.status === "active" && ` · ${(a.impressions || 0).toLocaleString()} views · ${(a.clicks || 0).toLocaleString()} clicks`}
                                                {a.expires_at && ` · ends ${new Date(a.expires_at).toLocaleDateString()}`}
                                            </p>
                                            <p className="text-xs text-zinc-500 mt-1 font-mono">{a.advertiser_email} · CTA: <a href={a.cta_url} target="_blank" rel="noopener noreferrer" className="link-underline">{a.cta_url}</a></p>
                                            {a.admin_note && <p className="text-xs text-red-600 mt-1 italic">Note: {a.admin_note}</p>}
                                        </div>
                                        <div className="flex gap-1 shrink-0">
                                            {a.status === "pending_review" && (
                                                <>
                                                    <Button onClick={() => moderateAd(a.campaign_id, "approved")} data-testid={`ad-approve-${a.campaign_id}`} className="rounded-sm h-8 px-3 text-xs bg-emerald-deep hover:bg-emerald-deep/90 text-white">Approve</Button>
                                                    <Button onClick={() => moderateAd(a.campaign_id, "rejected")} variant="outline" data-testid={`ad-reject-${a.campaign_id}`} className="rounded-sm h-8 px-3 text-xs border-red-300 text-red-600 hover:bg-red-50">Reject</Button>
                                                </>
                                            )}
                                            {a.status === "active" && (
                                                <Button onClick={() => moderateAd(a.campaign_id, "inactive")} variant="outline" data-testid={`ad-pause-${a.campaign_id}`} className="rounded-sm h-8 px-3 text-xs border-[#DDD6FE]">Take down</Button>
                                            )}
                                            {a.status === "rejected" && (
                                                <Button onClick={() => moderateAd(a.campaign_id, "approved")} variant="outline" data-testid={`ad-reapprove-${a.campaign_id}`} className="rounded-sm h-8 px-3 text-xs border-[#DDD6FE]">Approve</Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            )}

            {tab === "mobile" && (
                <div className="space-y-3" data-testid="admin-mobile">
                    <div className="flex items-center justify-between gap-3 border border-[#DDD6FE] rounded-sm bg-paper px-4 py-3">
                        <div>
                            <p className="text-sm font-medium">Port-day alerts</p>
                            <p className="text-xs text-zinc-500">Send today's "your number transfers today" push + SMS to any line whose transfer completes today.</p>
                        </div>
                        <Button onClick={runPortDayAlerts} data-testid="admin-run-port-day-alerts" className="bg-[#4C1D95] text-white shrink-0">Send now</Button>
                    </div>

                    <div className="border border-[#D97706]/30 rounded-sm bg-paper p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="admin-pay-first-card">
                        <div>
                            <p className="text-sm font-medium">Pay-first protection</p>
                            <p className="text-xs text-zinc-500">No eSIM, number, or port is bought from Telnyx until the customer has paid. Unpaid lines are paused automatically.</p>
                            <label className="flex items-center gap-2 mt-2 text-xs text-zinc-700 cursor-pointer">
                                <input type="checkbox" checked={adminOverride} onChange={(e) => setAdminOverride(e.target.checked)} data-testid="admin-override-checkbox" />
                                Admin override — let my next attach / re-issue go through even if the customer hasn't paid
                            </label>
                        </div>
                        <Button onClick={sweepUnpaid} data-testid="admin-sweep-unpaid-btn" className="bg-[#D97706] text-white shrink-0">Pause unpaid lines now</Button>
                    </div>

                    <PayFirstPanel refreshKey={mobileSubs} onChange={load} />

                    {/* Attach a ported line that isn't showing in a customer's app */}
                    <div className="border border-[#DDD6FE] rounded-sm bg-paper p-5" data-testid="admin-attach-line">
                        <p className="text-sm font-medium">Attach a ported line</p>
                        <p className="text-xs text-zinc-500 mb-3">Use this when a number ported on Telnyx but never showed up in the customer's app. It creates &amp; activates their wireless line, links the eSIM (pulls the QR), and wires it for in-app calling/texting.</p>
                        <div className="grid sm:grid-cols-2 gap-2">
                            <input value={attachForm.email} onChange={(e) => setAttachForm({ ...attachForm, email: e.target.value })} placeholder="Customer email" data-testid="attach-email" className="border border-[#DDD6FE] rounded-sm px-3 h-10 text-sm bg-white outline-none focus:border-[#4C1D95]" />
                            <input value={attachForm.phone_number} onChange={(e) => setAttachForm({ ...attachForm, phone_number: e.target.value })} placeholder="Phone number (e.g. 9015818897)" data-testid="attach-phone" className="border border-[#DDD6FE] rounded-sm px-3 h-10 text-sm bg-white outline-none focus:border-[#4C1D95]" />
                            <select value={attachForm.plan_id} onChange={(e) => setAttachForm({ ...attachForm, plan_id: e.target.value })} data-testid="attach-plan" className="border border-[#DDD6FE] rounded-sm px-3 h-10 text-sm bg-white outline-none focus:border-[#4C1D95]">
                                <option value="flex_1gb">Flex 1GB ($19.99)</option>
                                <option value="flex_3gb">Flex 3GB ($29.99)</option>
                                <option value="flex_5gb">Flex 5GB ($39.99)</option>
                                <option value="flex_payg">Flex Pay-As-You-Go ($19.99 + $15/GB)</option>
                            </select>
                            <input value={attachForm.esim_iccid} onChange={(e) => setAttachForm({ ...attachForm, esim_iccid: e.target.value })} placeholder="eSIM ICCID (optional)" data-testid="attach-iccid" className="border border-[#DDD6FE] rounded-sm px-3 h-10 text-sm bg-white outline-none focus:border-[#4C1D95]" />
                            <input value={attachForm.esim_sim_id} onChange={(e) => setAttachForm({ ...attachForm, esim_sim_id: e.target.value })} placeholder="eSIM Telnyx SIM ID (optional)" data-testid="attach-simid" className="border border-[#DDD6FE] rounded-sm px-3 h-10 text-sm bg-white outline-none focus:border-[#4C1D95] sm:col-span-2" />
                        </div>
                        <Button onClick={attachLine} disabled={attaching} data-testid="attach-submit" className="bg-brand-green text-white mt-3">{attaching ? "Attaching…" : "Attach & activate line"}</Button>
                    </div>

                    {mobileSubs.length === 0 ? (
                        <div className="border border-[#DDD6FE] rounded-sm bg-paper p-10 text-center text-sm text-zinc-500">No Flex Line Mobile subscriptions yet.</div>
                    ) : mobileSubs.map((m) => (
                        <div key={m.mobile_sub_id} className="border border-[#DDD6FE] rounded-sm bg-paper p-5" data-testid={`admin-mobile-${m.mobile_sub_id}`}>
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-display text-lg">{m.phone_number || "—"}</span>
                                        <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 rounded-sm border border-[#DDD6FE] text-zinc-500">{m.plan_id}</span>
                                        <span className={`font-mono text-[10px] uppercase px-1.5 py-0.5 rounded-sm border ${m.status === "active" ? "text-emerald-deep border-emerald-deep" : "text-zinc-500 border-[#DDD6FE]"}`}>{m.status}</span>
                                        {m.paid === false && <span data-testid={`admin-unpaid-${m.mobile_sub_id}`} className="font-mono text-[10px] uppercase px-1.5 py-0.5 rounded-sm border border-[#D97706] text-[#D97706]">unpaid</span>}
                                        {m.charged_total !== undefined && <span data-testid={`admin-charged-${m.mobile_sub_id}`} className="font-mono text-[10px] uppercase px-1.5 py-0.5 rounded-sm border border-[#DDD6FE] text-zinc-600">{`$${Number(m.charged_total).toFixed(2)} charged`}</span>}
                                        {m.payment_override && <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 rounded-sm border border-amber-300 text-amber-600">exempt</span>}
                                        {m.provisioning_mode === "test" && <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 rounded-sm border border-amber-300 text-amber-600">test</span>}
                                    </div>
                                    <p className="text-xs text-zinc-500 mt-1 font-mono">{m.user_email} · {m.provision_type} · eSIM {m.esim?.status || "—"}{m.provision_type === "port" ? ` · port ${m.port?.status || "—"}` : ""}</p>
                                    {m.provision_type === "port" && (
                                        <p className="text-xs text-zinc-500 mt-1 font-mono" data-testid={`admin-foc-${m.mobile_sub_id}`}>
                                            FOC / est. completion: {m.port?.foc_date ? new Date(m.port.foc_date).toLocaleDateString() : "pending carrier confirmation"}
                                        </p>
                                    )}
                                    {m.activation?.stages && (
                                        <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid={`admin-stages-${m.mobile_sub_id}`}>
                                            {m.activation.stages.map((s) => (
                                                <span key={s.key}
                                                    className={`font-mono text-[9px] uppercase px-1.5 py-0.5 rounded-sm border ${
                                                        s.state === "done" ? "text-brand-green border-brand-green bg-[#ECFDF5]"
                                                        : s.state === "current" ? "text-[#4C1D95] border-[#4C1D95]"
                                                        : "text-zinc-400 border-[#EDE7F6]"}`}>
                                                    {s.state === "done" ? "✓ " : s.state === "current" ? "• " : ""}{s.label}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-2 shrink-0">
                                    {m.provision_type === "port" && (
                                        <>
                                            <Button onClick={() => confirmPort(m.mobile_sub_id)} data-testid={`admin-confirm-port-${m.mobile_sub_id}`}
                                                variant="outline" className="rounded-sm h-9 text-xs border-[#DDD6FE]">Submit to carrier</Button>
                                            <Button onClick={() => fixPortMessaging(m.mobile_sub_id)} data-testid={`admin-fix-messaging-${m.mobile_sub_id}`}
                                                variant="outline" className="rounded-sm h-9 text-xs border-[#DDD6FE]">Fix messaging profile</Button>
                                            {!m.activation?.service_active && (
                                                <Button onClick={() => markPortComplete(m.mobile_sub_id)} data-testid={`admin-port-complete-${m.mobile_sub_id}`}
                                                    className="bg-brand-green text-white rounded-sm h-9 text-xs">Mark complete & activate</Button>
                                            )}
                                        </>
                                    )}
                                    {["active", "trialing", "past_due"].includes(m.status) && (
                                        <>
                                            <Button onClick={() => reissueEsim(m.mobile_sub_id)} data-testid={`admin-reissue-esim-${m.mobile_sub_id}`}
                                                variant="outline" className="rounded-sm h-9 text-xs border-[#DDD6FE]">Resend eSIM</Button>
                                            <LineTestButton subId={m.mobile_sub_id} />
                                            <Button onClick={() => reissueEsim(m.mobile_sub_id, true)} data-testid={`admin-replace-esim-${m.mobile_sub_id}`}
                                                variant="outline" className="rounded-sm h-9 text-xs border-[#D97706] text-[#D97706]">Replace eSIM</Button>
                                        </>
                                    )}
                                    {m.esim?.installation_status && <span className="text-[11px] font-mono text-zinc-500 self-center" data-testid={`admin-esim-install-${m.mobile_sub_id}`}>eSIM on phone: {m.esim.installation_status}</span>}
                                    {m.paid === false && (
                                        <Button onClick={() => chargeOrPause(m.mobile_sub_id, load)} data-testid={`admin-charge-${m.mobile_sub_id}`}
                                            className="bg-brand-green text-white rounded-sm h-9 text-xs">Charge card</Button>
                                    )}
                                    {m.paid !== null && m.paid !== undefined && (
                                        <Button onClick={() => togglePaymentExempt(m.mobile_sub_id, !m.payment_override)} data-testid={`admin-exempt-${m.mobile_sub_id}`}
                                            variant="outline" className="rounded-sm h-9 text-xs border-[#DDD6FE]">{m.payment_override ? "Remove exemption" : "Exempt from payment"}</Button>
                                    )}
                                    {(m.status === "past_due" || m.status === "suspended") && (
                                        <Button onClick={() => resendCardLink(m.mobile_sub_id)} data-testid={`admin-resend-card-${m.mobile_sub_id}`}
                                            className="bg-[#D97706] text-white rounded-sm h-9 text-xs">Resend card link</Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {tab === "api" && (
                <div className="space-y-6 max-w-3xl" data-testid="admin-api">
                    <div className="border border-[#DDD6FE] rounded-sm bg-paper p-6 space-y-5">
                        <div>
                            <h3 className="font-display text-xl">Public API keys</h3>
                            <p className="text-sm text-zinc-600 mt-1">
                                Generate a key so an external app (e.g. StreamVault) can call the Flex Line API.
                                Send it as the <code className="font-mono text-xs bg-cream px-1 rounded">X-API-Key</code> header.
                                Base URL: <code className="font-mono text-xs bg-cream px-1 rounded">https://flexlines.store/api/v1</code>
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                            <div className="flex-1">
                                <Label className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">Key name</Label>
                                <Input value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} placeholder="StreamVault production"
                                    data-testid="api-key-name-input" className="rounded-sm border-[#DDD6FE] h-10 mt-1.5" />
                            </div>
                            <div className="flex gap-2">
                                {["read", "write"].map((s) => (
                                    <button key={s} type="button" onClick={() => toggleScope(s)} data-testid={`api-scope-${s}`}
                                        className={`h-10 px-3 rounded-sm text-sm border ${newKeyScopes.includes(s) ? "bg-[#4C1D95] text-white border-[#4C1D95]" : "bg-white border-[#DDD6FE]"}`}>
                                        {s}
                                    </button>
                                ))}
                            </div>
                            <Button onClick={createApiKey} data-testid="create-api-key-btn" className="bg-black text-white rounded-sm h-10 px-5">
                                Generate key
                            </Button>
                        </div>

                        {createdKey && (
                            <div className="border border-emerald-deep/40 bg-emerald-deep/5 rounded-sm p-4" data-testid="created-key-banner">
                                <p className="text-sm font-medium text-emerald-deep">Copy your key now — it won't be shown again.</p>
                                <div className="flex items-center gap-2 mt-2">
                                    <code className="flex-1 font-mono text-xs bg-white border border-[#DDD6FE] rounded-sm px-3 py-2 break-all" data-testid="created-key-value">{createdKey.api_key}</code>
                                    <Button onClick={() => { navigator.clipboard.writeText(createdKey.api_key); toast.success("Copied"); }}
                                        data-testid="copy-api-key-btn" className="bg-[#4C1D95] text-white rounded-sm h-9 px-3 text-xs shrink-0">Copy</Button>
                                    <Button onClick={() => setCreatedKey(null)} variant="outline" className="rounded-sm h-9 px-3 text-xs border-[#DDD6FE] shrink-0">Done</Button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="space-y-2" data-testid="api-keys-list">
                        {apiKeys.length === 0 ? (
                            <div className="border border-[#DDD6FE] rounded-sm bg-paper p-8 text-center text-sm text-zinc-500">No API keys yet.</div>
                        ) : apiKeys.map((k) => (
                            <div key={k.key_id} className="border border-[#DDD6FE] rounded-sm bg-paper p-4 flex items-center justify-between gap-4" data-testid={`api-key-${k.key_id}`}>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-medium">{k.name}</span>
                                        {!k.active && <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 rounded-sm border border-red-300 text-red-600">revoked</span>}
                                        {(k.scopes || []).map((s) => <span key={s} className="font-mono text-[10px] uppercase px-1.5 py-0.5 rounded-sm border border-[#DDD6FE] text-zinc-500">{s}</span>)}
                                    </div>
                                    <p className="font-mono text-xs text-zinc-500 mt-1">{k.prefix}••••{k.last4} · {(k.request_count || 0).toLocaleString()} calls{k.last_used_at ? ` · last used ${new Date(k.last_used_at).toLocaleDateString()}` : " · never used"}</p>
                                </div>
                                {k.active && (
                                    <Button onClick={() => revokeApiKey(k.key_id)} variant="outline" data-testid={`revoke-key-${k.key_id}`}
                                        className="rounded-sm h-8 px-3 text-xs border-red-300 text-red-600 hover:bg-red-50 shrink-0">Revoke</Button>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="border border-[#DDD6FE] rounded-sm bg-cream/40 p-5 text-sm text-zinc-600">
                        <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Endpoints</p>
                        <ul className="space-y-1 font-mono text-xs">
                            <li><span className="text-emerald-deep">GET</span> /api/v1/numbers — list phone numbers</li>
                            <li><span className="text-emerald-deep">GET</span> /api/v1/users — list users</li>
                            <li><span className="text-emerald-deep">GET</span> /api/v1/messages?number_id= — list messages</li>
                            <li><span className="text-[#D97706]">POST</span> /api/v1/messages — send an SMS (needs <strong>write</strong> scope)</li>
                        </ul>
                    </div>
                </div>
            )}

            {tab === "settings" && (
                <div className="space-y-6 max-w-2xl" data-testid="admin-settings">
                    {/* Live mode + Telnyx balance at a glance */}
                    {telnyxStatus && (
                        <div className={`border rounded-sm p-5 flex items-center justify-between gap-4 ${telnyxStatus.live_provisioning ? "border-brand-green bg-[#ECFDF5]" : "border-[#DDD6FE] bg-cream/40"}`} data-testid="telnyx-mode-banner">
                            <div>
                                <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Provisioning mode</p>
                                <p className="font-display text-2xl font-light flex items-center gap-2">
                                    <span className={`w-2.5 h-2.5 rounded-full ${telnyxStatus.live_provisioning ? "bg-brand-green" : "bg-zinc-400"}`} />
                                    {telnyxStatus.live_provisioning ? "LIVE — real eSIMs & ports" : "TEST — simulated (nothing goes live)"}
                                </p>
                            </div>
                            <div className="text-right">
                                <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Telnyx balance</p>
                                <p className="font-medium" data-testid="telnyx-balance">
                                    {telnyxStatus.balance && telnyxStatus.balance.balance != null
                                        ? `$${telnyxStatus.balance.balance} ${telnyxStatus.balance.currency || ""}`
                                        : telnyxStatus.live_provisioning ? "—" : "n/a in test mode"}
                                </p>
                            </div>
                        </div>
                    )}
                    {telnyxStatus && telnyxStatus.live_provisioning && (
                        <WirelessHealth status={telnyxStatus} onDone={async () => {
                            try { const st = await api.get("/admin/telnyx/status"); setTelnyxStatus(st.data); } catch { /* ignore */ }
                        }} />
                    )}
                    {telnyxStatus && <TelnyxRouting />}
                    {telnyxStatus?.live_provisioning && <TelnyxCosts />}
                    {telnyxStatus?.configured && <PortOrders />}
                    {telnyxStatus?.configured && <ProfitDashboard />}
                    {/* Readiness banner */}
                    {telnyxReadiness && (
                        <div className={`border rounded-sm p-5 ${telnyxReadiness.fully_live ? "border-emerald-deep bg-[#ECFDF5]" : telnyxReadiness.ready_to_sell ? "border-[#C4B5FD] bg-[#F3E8FF]" : "border-[#D97706] bg-[#FEF3C7]"}`} data-testid="telnyx-readiness">
                            <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Porting & eSIM readiness</p>
                            <p className="font-medium mb-3">{telnyxReadiness.message}</p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                                <ReadyChip ok={telnyxReadiness.configured} label="API key" />
                                <ReadyChip ok={telnyxReadiness.number_search} label="Number search" />
                                <ReadyChip ok={telnyxReadiness.portability_check} label="Portability" />
                                <ReadyChip ok={telnyxReadiness.webhook_key} label="Webhook key" />
                                <ReadyChip ok={telnyxReadiness.live_provisioning} label="Live provisioning" />
                                <ReadyChip ok={telnyxReadiness.ready_to_sell} label="Ready to sell" />
                            </div>
                        </div>
                    )}

                    {/* Repair in-app calling (fixes outbound routing + inbound app ringing + SMS profile) */}
                    <div className="border border-[#DDD6FE] rounded-sm bg-paper p-6" data-testid="telnyx-repair-calling">
                        <h3 className="font-display text-xl">Repair in-app calling</h3>
                        <p className="text-sm text-zinc-600 mt-1 mb-4">
                            One tap to fix calling on the live account: attaches the Outbound Voice Profile (so <b>outgoing</b> calls
                            connect), routes your number to your WebRTC line (so <b>incoming</b> calls ring here in the app), and
                            re-assigns the messaging profile (so <b>texts</b> send &amp; receive). Run this after any Telnyx change,
                            then place a test call.
                        </p>
                        <button onClick={repairCalling} disabled={repairing} data-testid="repair-calling-btn"
                            className="bg-espresso text-white rounded-sm h-10 px-5 text-sm font-medium disabled:opacity-50">
                            {repairing ? "Repairing…" : "Repair calling now"}
                        </button>

                        <div className="mt-5 pt-5 border-t border-[#DDD6FE]">
                            <label className="text-sm font-medium">Ring my cell phone on incoming calls</label>
                            <p className="text-xs text-zinc-500 mt-1 mb-2">
                                Enter your personal cell number here to forward <strong>all</strong> your FlexLine numbers
                                (both Telnyx and Twilio lines) to it. Incoming calls ring your cell first; if unanswered
                                or unreachable, they fall back to voicemail. Leave blank to ring the in-app phone instead.
                            </p>
                            <div className="flex gap-2 max-w-md">
                                <input value={voiceFwd} onChange={(e) => setVoiceFwd(e.target.value)} placeholder="+1 901 520 1426"
                                    data-testid="voice-forward-input"
                                    className="flex-1 border border-[#DDD6FE] rounded-sm h-10 px-3 text-sm font-mono" />
                                <button onClick={saveVoiceFwd} disabled={savingFwd} data-testid="voice-forward-save-btn"
                                    className="border border-espresso/40 text-espresso rounded-sm h-10 px-4 text-sm font-medium disabled:opacity-50">
                                    {savingFwd ? "Saving…" : "Save"}
                                </button>
                            </div>
                        </div>

                        <div className="mt-5 pt-5 border-t border-[#DDD6FE]">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <label className="text-sm font-medium">Spam Shield — screen unknown callers</label>
                                    <p className="text-xs text-zinc-500 mt-1 max-w-md">
                                        When ON, incoming callers hear "press 1 to connect" before your phone or the app rings.
                                        Auto-dialers and robocalls hang up instead of ringing you — saving call minutes and balance.
                                        People you've texted or called before skip the gate automatically; unknown callers who don't
                                        press 1 go straight to voicemail (never lost).
                                    </p>
                                </div>
                                <button onClick={toggleSpamShield} disabled={savingShield} data-testid="spam-shield-toggle"
                                    role="switch" aria-checked={spamShield}
                                    className={`relative shrink-0 w-14 h-8 rounded-full transition-colors ${spamShield ? "bg-brand-green" : "bg-zinc-300"} disabled:opacity-50`}>
                                    <span className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${spamShield ? "translate-x-6" : ""}`} />
                                </button>
                            </div>
                        </div>

                        <div className="mt-5 pt-5 border-t border-[#DDD6FE]">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <label className="text-sm font-medium">Test / Sandbox mode</label>
                                    <p className="text-xs text-zinc-500 mt-1 max-w-md">
                                        Dry-run the full signup → eSIM → port flow on a test phone without creating a real
                                        customer or charging real money. Turn OFF for live customers.
                                    </p>
                                </div>
                                <button onClick={toggleSandbox} disabled={savingSandbox} data-testid="sandbox-toggle"
                                    role="switch" aria-checked={sandbox}
                                    className={`relative shrink-0 w-14 h-8 rounded-full transition-colors ${sandbox ? "bg-amber-500" : "bg-zinc-300"} disabled:opacity-50`}>
                                    <span className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${sandbox ? "translate-x-6" : ""}`} />
                                </button>
                            </div>
                        </div>

                        <div className="mt-5 pt-5 border-t border-[#DDD6FE]" data-testid="assign-esim-card">
                            <label className="text-sm font-medium">Activate a number on an eSIM (native carrier)</label>
                            <p className="text-xs text-zinc-500 mt-1 mb-3 max-w-md">
                                Make a ported/owned number the eSIM's native voice line so the phone works like regular
                                cell service — inbound rings the device, outbound shows this number. Goes live once VoLTE
                                is registered on the device. (In test mode this just records the request.)
                            </p>
                            <div className="flex flex-wrap gap-2 max-w-xl">
                                <input value={esimNumber} onChange={(e) => setEsimNumber(e.target.value)} placeholder="+1 901 581 8897"
                                    data-testid="assign-esim-number-input"
                                    className="flex-1 min-w-[160px] border border-[#DDD6FE] rounded-sm h-10 px-3 text-sm font-mono" />
                                <input value={esimIccid} onChange={(e) => setEsimIccid(e.target.value)} placeholder="eSIM ICCID (optional)"
                                    data-testid="assign-esim-iccid-input"
                                    className="flex-1 min-w-[160px] border border-[#DDD6FE] rounded-sm h-10 px-3 text-sm font-mono" />
                                <button onClick={assignNumberToEsim} disabled={assigningEsim} data-testid="assign-esim-btn"
                                    className="h-10 px-4 bg-brand-green text-white rounded-sm text-sm font-medium disabled:opacity-50">
                                    {assigningEsim ? "Activating…" : "Activate on eSIM"}
                                </button>
                            </div>
                        </div>

                        <div className="mt-5 pt-5 border-t border-[#DDD6FE]" data-testid="vip-contacts-card">
                            <label className="text-sm font-medium">VIP contacts — always ring through</label>
                            <p className="text-xs text-zinc-500 mt-1 mb-3 max-w-md">
                                These numbers skip the Spam Shield gate and ring you directly, even if you've never
                                talked before. Great for family, your boss, or a new client you're expecting.
                            </p>
                            <div className="flex gap-2 max-w-md">
                                <input value={vipNumber} onChange={(e) => setVipNumber(e.target.value)} placeholder="+1 555 123 4567"
                                    data-testid="vip-number-input"
                                    className="flex-1 border border-[#DDD6FE] rounded-sm h-10 px-3 text-sm font-mono" />
                                <input value={vipLabel} onChange={(e) => setVipLabel(e.target.value)} placeholder="Name (optional)"
                                    data-testid="vip-label-input"
                                    className="w-36 border border-[#DDD6FE] rounded-sm h-10 px-3 text-sm" />
                                <button onClick={addVip} disabled={addingVip} data-testid="vip-add-btn"
                                    className="border border-espresso/40 text-espresso rounded-sm h-10 px-4 text-sm font-medium disabled:opacity-50">
                                    {addingVip ? "Adding…" : "Add"}
                                </button>
                            </div>
                            {vipContacts.length > 0 && (
                                <ul className="mt-3 space-y-2 max-w-md" data-testid="vip-list">
                                    {vipContacts.map((c) => (
                                        <li key={c.number} data-testid={`vip-item-${c.number}`}
                                            className="flex items-center justify-between border border-[#DDD6FE] rounded-sm px-3 py-2 bg-cream/40">
                                            <span className="text-sm">
                                                <span className="font-mono">{c.number}</span>
                                                {c.label ? <span className="text-zinc-500 ml-2">· {c.label}</span> : null}
                                            </span>
                                            <button onClick={() => removeVip(c.number)} data-testid={`vip-remove-${c.number}`}
                                                className="text-xs text-emerald-deep hover:underline">Remove</button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        {repairResult && (
                            <div className="mt-4 border border-[#DDD6FE] rounded-sm bg-cream/50 p-4" data-testid="repair-result">
                                <p className="text-sm font-medium mb-2">{repairResult.ok ? "✓ Done" : "Completed with warnings"}</p>
                                <ul className="text-xs text-zinc-600 space-y-1 font-mono">
                                    {(repairResult.steps || []).map((s, i) => <li key={i}>• {s}</li>)}
                                </ul>
                            </div>
                        )}
                    </div>

                    <div className="border border-[#DDD6FE] rounded-sm bg-paper p-6 space-y-5">
                        <div>
                            <h3 className="font-display text-xl">Telnyx credentials</h3>
                            <p className="text-sm text-zinc-600 mt-1">
                                FlexLine Mobile runs eSIM provisioning, number porting, SMS and voice on Telnyx. Paste your keys
                                from the Telnyx portal (API Keys). Keys are stored securely and applied immediately — no redeploy needed.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 gap-4">
                            <div>
                                <label className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">Telnyx API key {telnyxSettings?.has_api_key && <span className="text-emerald-deep">· set ({telnyxSettings.api_key_masked})</span>}</label>
                                <input type="password" data-testid="telnyx-api-key-input" autoComplete="off"
                                    placeholder={telnyxSettings?.has_api_key ? "•••• (leave blank to keep)" : "KEY…"}
                                    value={telnyxForm.api_key} onChange={(e) => setTelnyxForm((f) => ({ ...f, api_key: e.target.value }))}
                                    className="w-full mt-1 border border-[#DDD6FE] rounded-sm h-10 px-3 text-sm bg-white" />
                            </div>
                            <div>
                                <label className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">Telnyx public key (webhooks) {telnyxSettings?.has_public_key && <span className="text-emerald-deep">· set</span>}</label>
                                <input type="password" data-testid="telnyx-public-key-input" autoComplete="off"
                                    placeholder={telnyxSettings?.has_public_key ? "•••• (leave blank to keep)" : "public key…"}
                                    value={telnyxForm.public_key} onChange={(e) => setTelnyxForm((f) => ({ ...f, public_key: e.target.value }))}
                                    className="w-full mt-1 border border-[#DDD6FE] rounded-sm h-10 px-3 text-sm bg-white" />
                            </div>
                        </div>
                        <Button onClick={() => saveTelnyx()} disabled={savingTelnyx} data-testid="save-telnyx-btn"
                            className="bg-black text-white rounded-sm h-10 px-6">
                            {savingTelnyx ? "Saving…" : "Save Telnyx keys"}
                        </Button>
                    </div>

                    <div className="border border-[#DDD6FE] rounded-sm bg-paper p-6 flex items-center justify-between gap-4" data-testid="telnyx-live-toggle-card">
                        <div>
                            <h3 className="font-display text-xl">Live provisioning</h3>
                            <p className="text-sm text-zinc-600 mt-1 max-w-md">
                                When ON, real eSIMs are issued and port orders are submitted to carriers (uses your Telnyx balance).
                                When OFF, checkouts still work but eSIMs/usage are simulated (safe test mode).
                            </p>
                        </div>
                        <button
                            data-testid="telnyx-live-toggle"
                            onClick={() => saveTelnyx({ live_provisioning: !telnyxSettings?.live_provisioning })}
                            className={`shrink-0 w-14 h-8 rounded-full transition-colors relative ${telnyxSettings?.live_provisioning ? "bg-brand-green" : "bg-zinc-300"}`}
                        >
                            <span className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all ${telnyxSettings?.live_provisioning ? "left-7" : "left-1"}`} />
                        </button>
                    </div>
                    <p className="text-xs text-zinc-500">⚠️ Keep your Telnyx balance funded before turning Live provisioning ON. This toggle now persists across restarts and redeploys — set it once and it stays.</p>
                </div>
            )}

            {tab === "studio" && <MarketingStudio />}

            {tab === "franchise" && <AdminFranchises />}

            {tab === "marketing" && (
                <div className="space-y-6 max-w-2xl" data-testid="admin-marketing">
                    <div className="border border-[#DDD6FE] rounded-sm bg-paper p-6 space-y-5">
                        <div>
                            <h3 className="font-display text-xl">Ad conversion tracking</h3>
                            <p className="text-sm text-zinc-600 mt-1">
                                Paste your tracking IDs so Google Ads, Meta and Bing can optimize campaigns toward real
                                signups, trials and purchases. Each pixel only loads when its ID is set. Changes apply on the next page load.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field label="Google Analytics 4 (GA4) ID" placeholder="G-XXXXXXXXXX" testid="mkt-ga4"
                                value={marketing.ga4_id} onChange={(v) => setMarketing((m) => ({ ...m, ga4_id: v }))} />
                            <Field label="Google Ads ID" placeholder="AW-XXXXXXXXXX" testid="mkt-google-ads"
                                value={marketing.google_ads_id} onChange={(v) => setMarketing((m) => ({ ...m, google_ads_id: v }))} />
                            <Field label="Google Ads conversion label" placeholder="abCdEfGhIjk" testid="mkt-google-ads-label"
                                value={marketing.google_ads_purchase_label} onChange={(v) => setMarketing((m) => ({ ...m, google_ads_purchase_label: v }))} />
                            <Field label="Meta (Facebook) Pixel ID" placeholder="1234567890" testid="mkt-meta-pixel"
                                value={marketing.meta_pixel_id} onChange={(v) => setMarketing((m) => ({ ...m, meta_pixel_id: v }))} />
                            <Field label="Microsoft / Bing UET tag ID" placeholder="1234567" testid="mkt-bing-uet"
                                value={marketing.bing_uet_id} onChange={(v) => setMarketing((m) => ({ ...m, bing_uet_id: v }))} />
                        </div>

                        <Button onClick={saveMarketing} disabled={savingMarketing} data-testid="save-marketing-btn"
                            className="bg-black text-white rounded-sm h-10 px-6">
                            {savingMarketing ? "Saving…" : "Save tracking IDs"}
                        </Button>
                    </div>

                    <div className="border border-[#DDD6FE] rounded-sm bg-cream/40 p-5 text-sm text-zinc-600">
                        <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
                            <Megaphone className="w-3.5 h-3.5" /> Conversions we report
                        </p>
                        <ul className="space-y-1 list-disc list-inside">
                            <li><span className="font-mono text-xs">sign_up</span> — new account created</li>
                            <li><span className="font-mono text-xs">start_trial</span> — 3-day free trial started</li>
                            <li><span className="font-mono text-xs">purchase</span> — paid subscription or number</li>
                        </ul>
                    </div>

                    <div className="border border-[#DDD6FE] rounded-sm bg-paper p-6 space-y-5">
                        <div>
                            <h3 className="font-display text-xl">Social media links</h3>
                            <p className="text-sm text-zinc-600 mt-1">
                                Paste your full profile URLs. Icons appear in the site footer for whatever you fill in.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field label="Instagram URL" placeholder="https://instagram.com/…" testid="social-instagram"
                                value={social.instagram} onChange={(v) => setSocial((s) => ({ ...s, instagram: v }))} />
                            <Field label="Facebook URL" placeholder="https://facebook.com/…" testid="social-facebook"
                                value={social.facebook} onChange={(v) => setSocial((s) => ({ ...s, facebook: v }))} />
                            <Field label="X (Twitter) URL" placeholder="https://x.com/…" testid="social-twitter"
                                value={social.twitter} onChange={(v) => setSocial((s) => ({ ...s, twitter: v }))} />
                            <Field label="TikTok URL" placeholder="https://tiktok.com/@…" testid="social-tiktok"
                                value={social.tiktok} onChange={(v) => setSocial((s) => ({ ...s, tiktok: v }))} />
                            <Field label="LinkedIn URL" placeholder="https://linkedin.com/company/…" testid="social-linkedin"
                                value={social.linkedin} onChange={(v) => setSocial((s) => ({ ...s, linkedin: v }))} />
                            <Field label="YouTube URL" placeholder="https://youtube.com/@…" testid="social-youtube"
                                value={social.youtube} onChange={(v) => setSocial((s) => ({ ...s, youtube: v }))} />
                        </div>
                        <Button onClick={saveSocial} disabled={savingSocial} data-testid="save-social-btn"
                            className="bg-black text-white rounded-sm h-10 px-6">
                            {savingSocial ? "Saving…" : "Save social links"}
                        </Button>
                    </div>
                </div>
            )}

            {/* Customer detail modal */}
            {detail && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setDetail(null)} role="dialog" aria-modal="true" aria-label="Customer record">
                    <div className="bg-paper border border-[#DDD6FE] rounded-sm max-w-2xl w-full mt-10 mb-10" onClick={(e) => e.stopPropagation()} data-testid="customer-detail-modal">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-[#DDD6FE]">
                            <h3 className="font-display text-xl">Customer record</h3>
                            <button onClick={() => setDetail(null)} data-testid="close-detail-modal"><X className="w-5 h-5 text-zinc-500" /></button>
                        </div>
                        <div className="p-6 space-y-6">
                            <section>
                                <h4 className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-3">Identity</h4>
                                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                                    <Row k="Email" v={detail.user.email} mono />
                                    <Row k="Account name" v={detail.user.name} />
                                    <Row k="Legal name" v={detail.user.full_name || "—"} />
                                    <Row k="Date of birth" v={detail.user.date_of_birth || "—"} mono />
                                    <Row k="SSN" v={detail.ssn || "Not on file"} mono testid="detail-ssn" />
                                    <Row k="KYC status" v={detail.user.profile_completed ? "Complete" : "Incomplete"} />
                                </dl>
                            </section>
                            <section>
                                <h4 className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-3">Address</h4>
                                <p className="text-sm">
                                    {detail.user.street_address ? (
                                        <>{detail.user.street_address}, {detail.user.city}, {detail.user.state} {detail.user.zip_code}</>
                                    ) : "—"}
                                </p>
                            </section>
                            <section>
                                <h4 className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-3">Service</h4>
                                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                                    <Row k="Plan" v={detail.user.plan} />
                                    <Row k="SMS credits" v={detail.user.sms_credits} mono />
                                    <Row k="Status" v={detail.user.is_terminated ? "TERMINATED" : "Active"} />
                                    <Row k="Joined" v={new Date(detail.user.created_at).toLocaleDateString()} mono />
                                </dl>
                                <div className="mt-3">
                                    <p className="text-xs text-zinc-500 mb-1">Phone numbers ({detail.numbers.length})</p>
                                    {detail.numbers.map((n) => (
                                        <span key={n.number_id} className={`inline-block font-mono text-xs mr-2 mb-1 px-2 py-0.5 border rounded-sm ${n.active ? "border-emerald-deep text-emerald-deep" : "border-[#DDD6FE] text-zinc-400 line-through"}`}>
                                            {n.phone_number}
                                        </span>
                                    ))}
                                    {detail.numbers.length === 0 && <span className="text-xs text-zinc-400">None</span>}
                                </div>
                            </section>
                            <section>
                                <h4 className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-3">Recent transactions</h4>
                                {detail.transactions.slice(0, 8).map((t) => (
                                    <div key={t.transaction_id || t.session_id} className="flex items-center justify-between text-xs py-1.5 border-b border-[#DDD6FE]/50">
                                        <span className="font-mono text-zinc-500">{new Date(t.created_at).toLocaleDateString()}</span>
                                        <span className="uppercase font-mono">{t.kind}</span>
                                        <span className="font-mono">${(t.amount || 0).toFixed(2)}</span>
                                        <span className={`font-mono uppercase ${t.payment_status === "paid" ? "text-emerald-deep" : "text-zinc-400"}`}>{t.payment_status}</span>
                                    </div>
                                ))}
                                {detail.transactions.length === 0 && <span className="text-xs text-zinc-400">None</span>}
                            </section>
                            {!detail.user.is_admin && !detail.user.is_terminated && (
                                <Button onClick={() => terminate(detail.user)} data-testid="modal-terminate-btn" className="w-full bg-[#D97706] hover:bg-[#B45309] text-white rounded-sm h-10">
                                    Terminate service
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function ReadyChip({ ok, label }) {
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-sm px-2 py-1 text-xs font-medium ${ok ? "bg-[#D6ECE2] text-emerald-deep" : "bg-zinc-100 text-zinc-500"}`} data-testid={`ready-${label.toLowerCase().replace(/[^a-z]/g, "")}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${ok ? "bg-emerald-deep" : "bg-zinc-400"}`} /> {label}
        </span>
    );
}


function Field({ label, placeholder, value, onChange, testid }) {
    return (
        <div className="space-y-1.5">
            <Label className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">{label}</Label>
            <Input
                value={value || ""}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                data-testid={`${testid}-input`}
                className="rounded-sm border-[#DDD6FE] font-mono text-sm h-10"
            />
        </div>
    );
}

function Row({ k, v, mono, testid }) {    return (
        <div className="flex justify-between gap-4">
            <dt className="text-zinc-500">{k}</dt>
            <dd className={mono ? "font-mono" : ""} data-testid={testid}>{v}</dd>
        </div>
    );
}

function Stat({ label, value, icon: Icon, highlight }) {
    return (
        <div className="bg-paper p-6">
            <div className="flex items-start justify-between">
                <p className="font-mono text-[11px] uppercase text-zinc-500">{label}</p>
                <Icon className={`w-4 h-4 ${highlight ? "text-emerald-deep" : "text-zinc-400"}`} />
            </div>
            <div className="font-display text-4xl font-light mt-3 tracking-tight">{value}</div>
        </div>
    );
}
const Th = ({ children }) => <th className="text-left px-5 py-3 font-mono text-[10px] uppercase text-zinc-500">{children}</th>;
const Td = ({ children }) => <td className="px-5 py-3 text-sm">{children}</td>;


function WirelessHealth({ status, onDone }) {
    const [busy, setBusy] = React.useState(false);
    const sims = status.sims || { total: 0, by_status: {}, blocked: [] };
    const blocked = sims.blocked?.length || 0;
    const bad = status.low_balance || blocked > 0;
    const repair = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/telnyx/wireless-repair");
            toast.success(`Repair done — ${data.unblocked} unblocked · ${data.esims_delivered} eSIMs emailed · ${data.voice_enabled} VoLTE enabled · ${data.numbers_wired} numbers wired`);
            if (data.spend_paused) toast.warning("Paid actions paused (new eSIMs, VoLTE) until the Telnyx balance is above the safety threshold.");
            if (data.alerts?.length) toast.warning(data.alerts.join(" · "));
            await onDone();
        } catch (e) { toast.error(e?.response?.data?.detail || "Repair failed"); }
        setBusy(false);
    };
    return (
        <div className={`border rounded-sm p-5 ${bad ? "border-[#D97706] bg-[#FEF3C7]" : "border-brand-green bg-[#ECFDF5]"}`} data-testid="wireless-health">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Customer eSIM health</p>
                    <p className="font-medium" data-testid="wireless-health-summary">
                        {sims.total} eSIMs · {Object.entries(sims.by_status || {}).map(([k, v]) => `${v} ${k}`).join(" · ") || "none"}
                    </p>
                    {blocked > 0 && <p className="text-sm text-[#D97706] mt-1" data-testid="wireless-blocked-warning">{blocked} eSIM(s) blocked by Telnyx for lack of funds — those customers have no service.</p>}
                    {status.low_balance && <p className="text-sm text-[#D97706] mt-1" data-testid="wireless-low-balance">Telnyx balance is under ${status.low_balance_threshold}. Top up and turn on Auto-Recharge at portal.telnyx.com → Billing so eSIMs never get blocked.</p>}
                    <p className="text-xs text-zinc-500 mt-2">Runs automatically every hour: emails pending eSIM QR codes, turns on VoLTE, wires purchased numbers, and re-enables blocked eSIMs once funded.</p>
                </div>
                <Button onClick={repair} disabled={busy} data-testid="wireless-repair-btn" className="bg-[#4C1D95] text-white rounded-sm h-9 shrink-0">
                    {busy ? "Repairing…" : "Repair all lines now"}
                </Button>
            </div>
        </div>
    );
}


const INBOUND_BIN = `<?xml version="1.0" encoding="UTF-8"?>
<Response>
  <Dial timeout="25">
    <Number>{{Ring}}</Number>
  </Dial>
  <Say voice="female">You've reached voicemail. No one is available to take your call right now. Please leave your name, number, and a brief message after the tone, and your call will be returned. Thank you.</Say>
  <Record maxLength="120" playBeep="true" recordingStatusCallback="{{VmCb}}" recordingStatusCallbackMethod="POST"/>
</Response>`;

const OUTBOUND_BIN = `<?xml version="1.0" encoding="UTF-8"?>
<Response>
  <Dial callerId="{{CallerId}}">
    <Number>{{To}}</Number>
  </Dial>
</Response>`;

function TelnyxRouting() {
    const [cfg, setCfg] = React.useState(null);
    const [inUrl, setInUrl] = React.useState("");
    const [outUrl, setOutUrl] = React.useState("");
    const [busy, setBusy] = React.useState(false);
    const load = async () => {
        try {
            const { data } = await api.get("/admin/telnyx/texml-bins");
            setCfg(data); setInUrl(data.inbound_url); setOutUrl(data.outbound_url);
        } catch { /* ignore */ }
    };
    React.useEffect(() => { load(); }, []);
    const save = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/telnyx/texml-bins", { inbound_url: inUrl, outbound_url: outUrl });
            toast.success(`Saved — ${data.lines_moved_to_telnyx} line(s) moved to Telnyx-only routing`);
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
        setBusy(false);
    };
    const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };
    const ready = cfg?.inbound_url && cfg?.outbound_url;
    return (
        <div className="border border-[#DDD6FE] bg-paper rounded-sm p-5 space-y-4" data-testid="telnyx-routing-card">
            <div>
                <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Carrier call routing (runs on Telnyx)</p>
                <p className="font-medium" data-testid="telnyx-routing-status">
                    {ready ? `Active — ${cfg.lines_on_telnyx} line(s) on Telnyx-only routing, ${cfg.lines_on_server} waiting` : "Not set up yet — lines use FlexLine server routing"}
                </p>
                <p className="text-xs text-zinc-500 mt-1">One-time setup: in portal.telnyx.com → Voice → TeXML Bins, create two bins with the code below, then paste their URLs here. Calls to customers' numbers will ring their phones and dial-pad calls will show their number — with no FlexLine server in the call path.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
                {[["Inbound bin (ring phone + voicemail)", INBOUND_BIN, "inbound"], ["Outbound bin (caller ID)", OUTBOUND_BIN, "outbound"]].map(([label, code, k]) => (
                    <div key={k}>
                        <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium">{label}</span>
                            <button onClick={() => copy(code)} className="text-xs text-[#4C1D95] hover:underline" data-testid={`copy-${k}-bin`}>Copy code</button>
                        </div>
                        <pre className="text-[10px] bg-[#F8F5FC] border border-[#DDD6FE] rounded-sm p-2 overflow-x-auto whitespace-pre-wrap">{code}</pre>
                    </div>
                ))}
            </div>
            <div className="grid md:grid-cols-2 gap-3">
                <Input value={inUrl} onChange={(e) => setInUrl(e.target.value)} placeholder="Inbound bin URL (https://...)" data-testid="texml-inbound-url" />
                <Input value={outUrl} onChange={(e) => setOutUrl(e.target.value)} placeholder="Outbound bin URL (https://...)" data-testid="texml-outbound-url" />
            </div>
            <Button onClick={save} disabled={busy || !inUrl || !outUrl} data-testid="texml-save-btn" className="bg-[#4C1D95] text-white rounded-sm h-9">
                {busy ? "Saving…" : "Save & move lines to Telnyx routing"}
            </Button>
        </div>
    );
}


function LineTestButton({ subId }) {
    const [state, setState] = React.useState("");
    const run = async () => {
        if (!window.confirm("Place a real test call to this customer's number now? It will ring their phone (or go to voicemail).")) return;
        setState("Dialing…");
        try {
            const { data } = await api.post(`/admin/mobile/${subId}/test-call`);
            const id = encodeURIComponent(data.call_control_id);
            for (let i = 0; i < 9; i++) {
                await new Promise((r) => setTimeout(r, 5000));
                const { data: st } = await api.get(`/admin/mobile/test-call/${id}`);
                setState(`${st.seconds}s · ${st.verdict}`);
                if (!st.alive) { toast.error(`Not answered — the call ended after ${st.seconds}s. Check the line's routing and eSIM.`); break; }
                if (st.seconds > 30) { toast.success("Test call answered — the line works"); break; }
            }
        } catch (e) { setState(""); toast.error(e?.response?.data?.detail || "Test call failed"); }
    };
    return (
        <>
            <Button onClick={run} data-testid={`admin-test-call-${subId}`} variant="outline" className="rounded-sm h-9 text-xs border-[#4C1D95] text-[#4C1D95]">Test call</Button>
            {state && <span className="text-[11px] font-mono text-zinc-600 self-center" data-testid={`admin-test-call-status-${subId}`}>{state}</span>}
        </>
    );
}

function TelnyxCosts() {
    const [days, setDays] = React.useState(1);
    const [d, setD] = React.useState(null);
    React.useEffect(() => { setD(null); api.get(`/admin/telnyx/costs?days=${days}`).then(({ data }) => setD(data)).catch(() => setD({ error: true })); }, [days]);
    return (
        <div className="border border-[#DDD6FE] bg-paper rounded-sm p-5" data-testid="telnyx-costs">
            <div className="flex items-center justify-between mb-3">
                <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">Telnyx cost breakdown</p>
                <div className="flex gap-1">
                    {[1, 7, 30].map((n) => (
                        <button key={n} onClick={() => setDays(n)} data-testid={`costs-days-${n}`}
                            className={`text-xs px-2 h-7 rounded-sm border ${days === n ? "bg-[#4C1D95] text-white border-[#4C1D95]" : "border-[#DDD6FE]"}`}>{n === 1 ? "Today" : `${n} days`}</button>
                    ))}
                </div>
            </div>
            {!d ? <p className="text-sm text-zinc-500">Loading…</p> : d.error ? <p className="text-sm text-[#D97706]">Couldn't load costs</p> : (
                <div className="grid md:grid-cols-2 gap-6 text-sm">
                    <div data-testid="costs-usage">
                        <p className="font-medium mb-1">Usage ({days === 1 ? "last 24h" : `last ${days} days`}): ${d.usage_total.toFixed(2)}</p>
                        {Object.entries(d.usage).map(([k, v]) => <div key={k} className="flex justify-between text-zinc-600"><span>{k}</span><span className="font-mono">${v.toFixed(2)}</span></div>)}
                    </div>
                    <div data-testid="costs-recurring">
                        <p className="font-medium mb-1">Monthly fees (estimate): ${d.recurring_total.toFixed(2)}/mo</p>
                        {Object.entries(d.recurring_monthly_estimate).map(([k, v]) => <div key={k} className="flex justify-between text-zinc-600"><span>{k}</span><span className="font-mono">${v.toFixed(2)}</span></div>)}
                        <p className="text-[11px] text-zinc-500 mt-2">Telnyx charges these fees when a line is turned on, which is why the balance can drop in steps.</p>
                    </div>
                </div>
            )}
        </div>
    );
}


function ProfitDashboard() {
    const [d, setD] = React.useState(null);
    React.useEffect(() => { api.get("/admin/mobile/profit").then(({ data }) => setD(data)).catch(() => setD({ error: true })); }, []);
    const money = (v) => `${v < 0 ? "-" : ""}$${Math.abs(v).toFixed(2)}`;
    return (
        <div className="border border-[#DDD6FE] bg-paper rounded-sm p-5" data-testid="profit-dashboard">
            <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Profit per line (this month)</p>
            {!d ? <p className="text-sm text-zinc-500">Loading…</p> : d.error ? <p className="text-sm text-[#D97706]">Couldn't load profit</p> : (
                <>
                    <p className="text-sm mb-3" data-testid="profit-totals">
                        Revenue <strong>{money(d.revenue)}</strong> · Telnyx + card costs <strong>{money(d.cost)}</strong> · Profit{" "}
                        <strong className={d.profit < 0 ? "text-[#D97706]" : "text-emerald-deep"}>{money(d.profit)}</strong>
                        <span className="text-zinc-500"> · data this month {d.data_mb_this_month} MB at ${d.data_rate_per_gb}/GB</span>
                    </p>
                    <div className="max-h-72 overflow-y-auto text-xs">
                        <table className="w-full">
                            <thead><tr className="text-left text-zinc-500"><th>Line</th><th>Plan</th><th>Status</th><th>Data</th><th>Revenue</th><th>Cost</th><th>Profit</th></tr></thead>
                            <tbody>
                                {d.lines.map((l) => (
                                    <tr key={l.mobile_sub_id} className="border-t border-[#F3E8FF]" data-testid={`profit-line-${l.mobile_sub_id}`}>
                                        <td className="py-1 font-mono">{l.phone_number || "—"}<div className="text-zinc-500">{l.email}</div></td>
                                        <td>{l.plan}</td><td>{l.status}</td><td>{l.data_mb} MB</td>
                                        <td>{money(l.revenue)}</td><td>{money(l.cost)}</td>
                                        <td className={l.profit < 0 ? "text-[#D97706] font-medium" : "text-emerald-deep font-medium"}>{money(l.profit)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p className="text-[11px] text-zinc-500 mt-2">Free-trial lines earn $0 until their first bill. Costs: eSIM $2 (standby $0.20), phone calling $5, number $1, data at Telnyx's actual rate, card fee 2.9% + 30¢.</p>
                </>
            )}
        </div>
    );
}


function PortOrders() {
    const [rows, setRows] = React.useState(null);
    const load = () => api.get("/admin/porting/orders").then(({ data }) => setRows(data.orders)).catch(() => setRows([]));
    React.useEffect(() => { load(); }, []);
    const send = async (o) => {
        const email = window.prompt(`Email of ${o.name || "the number owner"} for the e-sign link:`, o.sign_request?.email || "");
        if (!email) return;
        try { const { data } = await api.post(`/admin/porting/${o.order_id}/request-signature`, { email }); toast.success("E-sign link sent"); navigator.clipboard?.writeText(data.link); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send link"); }
    };
    return (
        <div className="border border-[#DDD6FE] bg-paper rounded-sm p-5" data-testid="port-orders">
            <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Number transfers (all Telnyx porting orders)</p>
            {!rows ? <p className="text-sm text-zinc-500">Loading…</p> : rows.length === 0 ? <p className="text-sm text-zinc-500">No transfers yet.</p> : (
                <div className="space-y-2 text-sm">
                    {rows.map((o) => (
                        <div key={o.order_id} className="flex items-center justify-between gap-3 border-t border-[#F3E8FF] pt-2" data-testid={`port-order-${o.order_id}`}>
                            <div>
                                <p className="font-mono">{o.numbers.join(", ") || "—"} <span className="text-zinc-500">· {o.name}</span></p>
                                <p className="text-xs text-zinc-500">Status: <strong>{o.status}</strong>{o.foc ? ` · transfer date ${new Date(o.foc).toLocaleDateString()}` : ""}{o.sign_request ? ` · e-sign ${o.sign_request.status}` : ""}{o.sign_request?.error ? ` — ${o.sign_request.error}` : ""}</p>
                            </div>
                            {["draft", "exception", "in-process"].includes(o.status) && !o.requirements_met && (
                                <Button onClick={() => send(o)} data-testid={`port-send-sign-${o.order_id}`} variant="outline" className="rounded-sm h-8 text-xs border-[#4C1D95] text-[#4C1D95] shrink-0">
                                    {o.sign_request ? "Resend e-sign link" : "Send e-sign link"}
                                </Button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
